import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { supabase } from "../../lib/supabase";
import { toOrgDateKey, formatDateTimeInTZ, getTimezoneAbbr } from "../../lib/timezone";
import ChatBubble from "./ChatBubble";
import { ReplyTimeline } from "./ReplyTimeline";
import { LeadJourneyEventCard } from "./LeadJourneyEventCard";
import type { LeadTimelineResult } from "../../types";

// =============================================================================
// ORPHAN PRE-CALLS MODAL — Infinity Gauntlet (4-column journey audit)
// =============================================================================
// PURPOSE: Audit a setter's orphan pre-calls (pre-calls without a matching call
// outcome) by showing the full lead journey across 4 synchronized columns:
//
//   ┌─ Orphan list ──┬─ Lead timeline ──┬─ Reply timeline ──┬─ Chat bubbles ──┐
//   │ Alice's 13     │ event chips:     │ per-message reply │ scrolled to the │
//   │ orphans —      │ webinar →        │ gaps + speed      │ scheduled call  │
//   │ click any      │ pre-call form →  │ annotations       │ time of the     │
//   │ row to swap    │ call_booked →    │ (existing comp.)  │ selected orphan │
//   │ the right 3    │ call_cancelled   │                   │                 │
//   │ columns        │ → SCO            │                   │                 │
//   └────────────────┴──────────────────┴───────────────────┴─────────────────┘
//
// "Machine-gun" pattern: rapid clicks through the orphan list — only the latest
// click wins (sequence counter cancels in-flight loads). Right-side columns
// each render their own independent skeleton; left list stays interactive.
//
// Why orphan-only and not generalized to all DM modals: the right 3 columns
// anchor on `lead.email`, which is only guaranteed for orphan pre-calls
// (pre-call form requires email validation). Generic DMs can be email-less.
// =============================================================================

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface OrphanPreCallRow {
  lead_name: string | null;
  lead_email: string | null;
  scheduled_call_time: string;
  diagnosis: "flakey" | "ghost" | "neglected";
  conversation_id: string | null;
}

interface DMMessage {
  id: string;
  message_text: string | null;
  sent_at: string;
  sender_type: "lead" | "setter" | string;
  has_media?: boolean;
  ghl_sender_user_id?: string | null;
}

export interface OrphanPreCallsModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  setterId: string;
  setterName: string;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
}

// -----------------------------------------------------------------------------
// Lead-timeline event chip (compact mini-version of LeadTimelineModal's
// TimelineItem — kept inline so this modal stays self-contained)
// -----------------------------------------------------------------------------

// Event labels, dot colors, and eventDate helper now live in LeadJourneyEventCard.

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function formatScheduledTime(isoString: string, tz: string): string {
  if (!isoString) return "--";
  const abbr = getTimezoneAbbr(tz);
  return `${formatDateTimeInTZ(isoString, tz)} ${abbr}`;
}

function diagnosisBadgeClass(d: string): string {
  const base = "token-10 px-1.5 py-0.5 rounded border shrink-0";
  if (d === "flakey") return `${base} bg-ds-amber/10 text-ds-amber border-ds-amber/30`;
  if (d === "ghost") return `${base} bg-ds-purple/10 text-ds-purple border-ds-purple/30`;
  return `${base} bg-ds-red/10 text-ds-red border-ds-red/30`;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const OrphanPreCallsModal: React.FC<OrphanPreCallsModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  setterId,
  setterName,
  dateRange,
  timezone,
}) => {
  // Orphan list state
  const [rows, setRows] = useState<OrphanPreCallRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // Selection + per-lead state
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [timeline, setTimeline] = useState<LeadTimelineResult | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [expandedJourneyIdx, setExpandedJourneyIdx] = useState<number | null>(null);
  const [messages, setMessages] = useState<DMMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  // Sequence counter — only the LATEST click writes results (machine-gun protection)
  const sequenceRef = useRef(0);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  const selected = selectedIdx != null ? rows[selectedIdx] : null;

  // ---------------------------------------------------------------------------
  // Fetch the orphan list when the modal opens
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen || !supabase || !organizationId || !setterId || !dateRange.start || !dateRange.end)
      return;
    let cancelled = false;
    setListLoading(true); // eslint-disable-line react-hooks/set-state-in-effect -- async fetch
    setListError(null);

    const startStr = toOrgDateKey(dateRange.start);
    const endStr = toOrgDateKey(dateRange.end);

    supabase
      .rpc("get_setter_orphan_pre_call_details", {
        p_organization_id: organizationId,
        p_setter_ghl_user_id: setterId,
        p_start_date: startStr,
        p_end_date: endStr,
      })
      .then(({ data, error: rpcError }) => {
        if (cancelled) return;
        if (rpcError) {
          setListError(rpcError.message);
          setListLoading(false);
          return;
        }
        const next = (data as OrphanPreCallRow[]) || [];
        setRows(next);
        setListLoading(false);
        // Auto-select the first row with both an email AND a conversation
        // (so the right columns aren't blank on first paint).
        const firstActionable = next.findIndex((r) => r.lead_email && r.conversation_id);
        if (firstActionable >= 0) setSelectedIdx(firstActionable);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, organizationId, setterId, dateRange.start, dateRange.end]);

  // ---------------------------------------------------------------------------
  // Reset on close
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) {
      setRows([]); // eslint-disable-line react-hooks/set-state-in-effect -- reset on close
      setListError(null);
      setSelectedIdx(null);
      setTimeline(null);
      setMessages([]);
      setChatError(null);
    }
  }, [isOpen]);

  // ---------------------------------------------------------------------------
  // ESC closes the modal (no nested modal anymore — single layer)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // ---------------------------------------------------------------------------
  // When the selected orphan changes, fetch lead timeline + chat in parallel.
  // sequence counter ensures only the latest selection wins.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!isOpen || !selected || !supabase) return;
    if (!selected.lead_email || !selected.conversation_id) {
      setTimeline(null); // eslint-disable-line react-hooks/set-state-in-effect -- clear stale state on unfetchable selection
      setMessages([]);
      return;
    }

    sequenceRef.current += 1;
    const mySeq = sequenceRef.current;

    setTimelineLoading(true);
    setChatLoading(true);
    setChatError(null);
    setExpandedJourneyIdx(null);
    hasAutoScrolledRef.current = false;

    // Lead timeline (left)
    supabase
      .rpc("get_lead_timeline", {
        p_email: selected.lead_email,
        p_org_id: organizationId,
      })
      .then(({ data, error: tlError }) => {
        if (mySeq !== sequenceRef.current) return; // a newer click won
        if (tlError) {
          setTimeline(null);
        } else {
          setTimeline(data as LeadTimelineResult);
        }
        setTimelineLoading(false);
      });

    // Messages (right)
    supabase
      .from("ghl_messages")
      .select("id, message_text, sent_at, sender_type, has_media, ghl_sender_user_id, message_type")
      .eq("conversation_id", selected.conversation_id)
      .not("message_type", "in", "(TYPE_ACTIVITY_OPPORTUNITY,TYPE_INTERNAL_COMMENT,TYPE_CALL)")
      .order("sent_at", { ascending: true })
      .limit(500)
      .then(({ data, error: msgError }) => {
        if (mySeq !== sequenceRef.current) return;
        if (msgError) {
          setChatError(msgError.message);
          setMessages([]);
          setChatLoading(false);
          return;
        }
        const filtered = (data || []).filter(
          (m) =>
            !m.message_text?.includes("Opportunity created") &&
            !m.message_text?.includes("Opportunity updated") &&
            !m.message_text?.includes("Opportunity deleted")
        );
        setMessages(filtered as DMMessage[]);
        setChatLoading(false);
      });
  }, [isOpen, selected, organizationId]);

  // ---------------------------------------------------------------------------
  // Auto-scroll the chat to the message closest to the scheduled call time.
  // The "where it went wrong" anchor — see Question 5 in the spec.
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (chatLoading || messages.length === 0 || !selected?.scheduled_call_time) return;
    if (hasAutoScrolledRef.current) return;
    hasAutoScrolledRef.current = true;

    const target = new Date(selected.scheduled_call_time).getTime();
    let closestIdx = 0;
    let closestDiff = Infinity;
    messages.forEach((m, i) => {
      const diff = Math.abs(new Date(m.sent_at).getTime() - target);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIdx = i;
      }
    });

    const container = messagesContainerRef.current;
    if (!container) return;
    const el = container.querySelector<HTMLElement>(
      `[data-message-id="${messages[closestIdx].id}"]`
    );
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  }, [chatLoading, messages, selected]);

  if (!mounted) return null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // Fixed inset edge-to-edge — 4 columns need the room. z-[9998] preserved
  // for parity with the prior modal contract (no nested modal anymore, but
  // future drill-throughs may want to layer above).
  return createPortal(
    <div className="fixed inset-0 z-[9998] flex flex-col">
      {/* Backdrop */}
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/60 backdrop-blur-sm`}
        onClick={onClose}
      />

      {/* Modal shell — anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div
        className={`${closing ? "anim-modal-out" : "anim-modal-in"} m-4 flex-1 flex flex-col min-h-0`}
      >
        <div className="relative detail-overlay flex-1 flex flex-col rounded-xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-black/[0.08] shrink-0">
            <div>
              <h2 className="token-18 text-glass">
                Orphan Pre-Calls — {setterName}{" "}
                <span className="token-13 text-glass-muted">
                  ({rows.length} {rows.length === 1 ? "lead" : "leads"})
                </span>
              </h2>
              <p className="token-12 text-glass-muted">
                Pre-calls without a matching call outcome — click a lead to inspect their journey
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap p-2 hover:bg-black/[0.05] rounded-lg transition-colors"
              aria-label="Close modal"
            >
              <svg
                className="w-5 h-5 text-glass-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* 4-column body */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            {/* COL 1 — Orphan list */}
            <div className="w-[260px] shrink-0 border-r border-black/[0.08] flex flex-col">
              <div className="px-4 py-2 border-b border-black/[0.06] shrink-0">
                <p className="token-10u text-glass-muted uppercase tracking-wide">Orphans</p>
              </div>
              <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
                {listLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ds-accent" />
                  </div>
                )}
                {!listLoading && listError && (
                  <div className="p-4 text-ds-red token-13">{listError}</div>
                )}
                {!listLoading && !listError && rows.length === 0 && (
                  <div className="text-center py-12 text-glass-muted token-13 px-4">
                    No orphan pre-calls in this range.
                  </div>
                )}
                {!listLoading && !listError && rows.length > 0 && (
                  <div className="divide-y divide-black/[0.05]">
                    {rows.map((row, idx) => {
                      const hasContext = !!row.lead_email && !!row.conversation_id;
                      const displayName = row.lead_name || row.lead_email || "Unknown";
                      const isActive = idx === selectedIdx;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => hasContext && setSelectedIdx(idx)}
                          disabled={!hasContext}
                          className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-2 ${
                            isActive
                              ? "bg-ds-accent/10 border-l-2 border-ds-accent"
                              : hasContext
                                ? "hover:bg-black/[0.03] border-l-2 border-transparent cursor-pointer"
                                : "opacity-50 cursor-not-allowed border-l-2 border-transparent"
                          }`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="token-13 text-glass truncate">{displayName}</div>
                            <div className="token-10 text-glass-muted truncate">
                              {formatScheduledTime(row.scheduled_call_time, timezone)}
                            </div>
                          </div>
                          <span className={diagnosisBadgeClass(row.diagnosis)}>
                            {row.diagnosis}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* COL 2 — Lead timeline */}
            <div className="w-[300px] shrink-0 border-r border-black/[0.08] flex flex-col">
              <div className="px-4 py-2 border-b border-black/[0.06] shrink-0">
                <p className="token-10u text-glass-muted uppercase tracking-wide">Lead Journey</p>
              </div>
              <div
                className="flex-1 overflow-y-auto px-4 py-3"
                style={{ overscrollBehavior: "contain" }}
              >
                {!selected && (
                  <p className="text-glass-muted token-13 text-center py-8">
                    Select a lead to see their journey.
                  </p>
                )}
                {selected && timelineLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ds-accent" />
                  </div>
                )}
                {selected && !timelineLoading && timeline && timeline.timeline.length === 0 && (
                  <p className="text-glass-muted token-13 text-center py-8">
                    No events on file for this lead.
                  </p>
                )}
                {selected && !timelineLoading && timeline && timeline.timeline.length > 0 && (
                  <div className="relative">
                    <div className="absolute left-[6px] top-2 bottom-2 w-px bg-black/[0.08]" />
                    <div className="space-y-3">
                      {timeline.timeline.map((event, idx) => (
                        <LeadJourneyEventCard
                          key={idx}
                          event={event}
                          expanded={expandedJourneyIdx === idx}
                          onToggle={() =>
                            setExpandedJourneyIdx((prev) => (prev === idx ? null : idx))
                          }
                          timezone={timezone}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* COL 3 — Reply timeline */}
            <div className="w-[260px] shrink-0 border-r border-black/[0.08] flex flex-col">
              <div className="px-4 py-2 border-b border-black/[0.06] shrink-0">
                <p className="token-10u text-glass-muted uppercase tracking-wide">Reply Timeline</p>
              </div>
              <div className="flex-1 flex flex-col min-h-0">
                {!selected && (
                  <p className="text-glass-muted token-13 text-center py-8 px-4">Select a lead.</p>
                )}
                {selected && chatLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ds-accent" />
                  </div>
                )}
                {selected && !chatLoading && messages.length > 0 && (
                  <ReplyTimeline
                    messages={messages.map((m) => ({
                      id: m.id,
                      message_text: m.message_text,
                      sent_at: m.sent_at,
                      sender_type: m.sender_type,
                      ghl_sender_user_id: m.ghl_sender_user_id ?? null,
                    }))}
                    timezone={timezone}
                    setterName={setterName}
                    contactName={selected.lead_name || selected.lead_email || "Lead"}
                    onEntryClick={(messageId) => {
                      const container = messagesContainerRef.current;
                      if (!container) return;
                      const el = container.querySelector<HTMLElement>(
                        `[data-message-id="${messageId}"]`
                      );
                      el?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                  />
                )}
                {selected && !chatLoading && messages.length === 0 && (
                  <p className="text-glass-muted token-13 text-center py-8 px-4">No messages.</p>
                )}
              </div>
            </div>

            {/* COL 4 — Chat bubbles */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="px-4 py-2 border-b border-black/[0.06] shrink-0 flex items-center justify-between">
                <p className="token-10u text-glass-muted uppercase tracking-wide">Conversation</p>
                {selected?.scheduled_call_time && (
                  <p className="token-10 text-glass-muted">
                    Scheduled call: {formatScheduledTime(selected.scheduled_call_time, timezone)}
                  </p>
                )}
              </div>
              <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto bg-black/[0.03] p-4"
                style={{ overscrollBehavior: "contain" }}
              >
                {!selected && (
                  <p className="text-glass-muted token-13 text-center py-12">
                    Select a lead from the left to see their conversation.
                  </p>
                )}
                {selected && chatLoading && (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-6 w-6 rounded-full border-b-2 border-ds-accent" />
                  </div>
                )}
                {selected && !chatLoading && chatError && (
                  <div className="text-ds-red token-13 text-center py-8">{chatError}</div>
                )}
                {selected && !chatLoading && !chatError && messages.length === 0 && (
                  <p className="text-glass-muted token-13 text-center py-8">
                    No messages found for this conversation.
                  </p>
                )}
                {selected && !chatLoading && !chatError && messages.length > 0 && (
                  <>
                    {messages.map((m) => {
                      const isClosestToCall =
                        selected.scheduled_call_time &&
                        Math.abs(
                          new Date(m.sent_at).getTime() -
                            new Date(selected.scheduled_call_time).getTime()
                        ) < 60_000;
                      return (
                        <div
                          key={m.id}
                          data-message-id={m.id}
                          ref={isClosestToCall ? highlightRef : undefined}
                        >
                          <ChatBubble
                            message={{
                              text: m.message_text,
                              sender: m.sender_type,
                              sent_at: m.sent_at,
                              has_media: m.has_media,
                            }}
                            timezone={timezone}
                            highlightType={isClosestToCall ? "highlight" : undefined}
                          />
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OrphanPreCallsModal;
